import { get, put, remove } from '../lib'

export type UserSummary = {
  id: number
  username: string
  firstName: string
  lastName: string
}

export type VehicleSummary = {
  id: number
  name: string
  displayName: string
  vin: string
  make: string
  model: string
  year: number
  fleetId: number
}

export type DeviceSummary = {
  id: number
  serial: string
  name: string
  type: string
}

export type VehicleCertStatus =
  | 'requested'
  | 'needs_approval'
  | 'issued'
  | 'installed'
  | 'pending_revocation'
  | 'revoked'
  | 'expired'

export type VehicleCert = {
  id: number
  status: VehicleCertStatus
  tmpCert: string | null
  certExpiresAt: string | null
  requestedAt: string
  issuedAt: string | null
  deletedAt: string | null
  requestedBy: UserSummary
  issuedBy: UserSummary | null
  revokedBy: UserSummary | null
  vehicle: VehicleSummary
  device: DeviceSummary
}

export type VehicleCertQuery = {
  status?: string[]
  sort?: string
  order?: 'asc' | 'desc'
  offset?: number
  max?: number
  vehicleId?: number
  showAll?: boolean
  certExpiresAtAfter?: string
  certExpiresAtBefore?: string
  deletedAtAfter?: string
  deletedAtBefore?: string
}

export type VehicleCertPage = {
  data: VehicleCert[]
  paging: { max: number; offset: number; total: number }
}

export const getService29VehicleCerts = async (
  query: VehicleCertQuery = {}
): Promise<VehicleCertPage> => {
  const { status, showAll, ...scalar } = query

  const params = new URLSearchParams()
  Object.entries(scalar).forEach(([k, v]) => {
    if (v !== undefined) params.append(k, String(v))
  })
  if (showAll) params.append('showAll', 'true')
  status?.forEach((s) => params.append('status', s))

  const qs = params.toString()
  const res = await get<{ data: VehicleCert[]; headers: Headers }>({
    path: `/api/v1/vehicleCerts${qs ? '?' + qs : ''}`,
    includeHeaders: true
  })

  return {
    data: res.data,
    paging: {
      max: Number(res.headers.get('x-wivi-max')) || query.max || 50,
      offset: Number(res.headers.get('x-wivi-offset')) || query.offset || 0,
      total: Number(res.headers.get('x-wivi-total')) || 0
    }
  }
}

export const approveVehicleCert = async (id: number) =>
  put<Partial<VehicleCert>, VehicleCert>({
    path: `/api/v1/vehicleCerts/${id}`,
    body: { status: 'issued' }
  })

export const revokeVehicleCert = async (id: number) =>
  remove<number, void>({
    path: `/api/v1/vehicleCerts`,
    id
  })
